<template>
	<AppTable
		ref="orders-table"
		:table-headings="tableHeadings"
		:title="getString('orders')"
		:total-items="parsedOrders.datesDivision.length"
		:items="currentShownOrders"
		:filters="filters"
		@changed-page="v=>currentPage = v"
		@changed-page-size="v=>pageSize = v"
		@changed-sort="changeSort"
		@changed-filter="changeFilter"
		allow-download
		@export="exportOrders"
		class="mb-30"
	/>

	<AppTable
		ref="orders-mode-table"
		:table-headings="tableHeadingsModes"
		:title="getString('order_mode')"
		:total-items="parsedOrders.orderModes.length"
		:items="currentShownOrderModes"
		:filters="filtersModes"
		@changed-sort="changeSortModes"
		@changed-filter="changeFilterModes"
		allow-download
		@export="exportModes"
		:app-select-values="['25']"
		class="mb-30"
	/>

	<AppTable
		ref="orders-type-table"
		:table-headings="tableHeadingsTypes"
		:title="getString('payment_type')"
		:total-items="parsedOrders.orderTypes.length"
		:items="currentShownOrderTypes"
		:filters="filtersTypes"
		@changed-sort="changeSortTypes"
		@changed-filter="changeFilterTypes"
		allow-download
		@export="exportTypes"
		:app-select-values="['25']"
		class="mb-30"
	/>

	<AppTable
		v-if="userStore.currPlans.counter"
		ref="orders-staff-table"
		:table-headings="tableHeadingsUsers"
		:title="getString('staff')"
		:total-items="parsedOrders.staffOrders.length"
		:items="currentShownStaff"
		:filters="filtersStaff"
		@changed-page="v=>staffCurrentPage = v"
		@changed-page-size="v=>staffPageSize = v"
		@changed-sort="changeSortStaff"
		@changed-filter="changeFilterStaff"
		allow-download
		@export="exportStaff"
		:info-button="getString('info_table_staff')"
		class="mb-30"
	>
		<template v-slot:custom-header>
			<div class="header-title-container">
				<h3>{{ getString('staff') }}</h3>
				<AppInfoButton v-if="getString('info_table_staff')">
					{{ getString('info_table_staff') }}
				</AppInfoButton>
			</div>
			<div style="display: flex; gap: 10px; align-items: center;">
				<AppButtonIcon
					prevent-change
					color="var(--color-bg-primary-1)"
					:icon-name="staffViewMode === 'creation' ? 'Icon-User-blue' : 'icon-money-blue'"
					:title="staffViewMode === 'creation' ? getString('order_creation') : getString('payment')"
					@click="toggleStaffView"
				/>
				<AppButtonIcon
					prevent-change
					color="var(--color-bg-primary-1)"
					icon-name="icon-download-blue"
					@click="$refs['orders-staff-table'].toggleExportType()"
				/>
			</div>
		</template>
	</AppTable>

	<AppTable
		v-if="userStore.currPlans.counter"
		ref="orders-additional-costs-table"
		:table-headings="tableHeadingsAdditionalCosts"
		:title="getString('additional_costs')"
		:total-items="parsedOrders.additionalOrderCosts.length"
		:items="parsedOrders.additionalOrderCosts"
		:filters="filtersAdditionalCosts"
		@changed-sort="changeSortAdditionalCosts"
		@changed-filter="changeFilterAdditionalCosts"
		allow-download
		@export="exportAdditionalCosts"
		class="mb-30"
	/>

</template>

<script>
import {useCurrencyStore, useLanguageStore, useNotificationStore, useValuesStore, useScriptStore} from "@/shared/stores";
import {useAnalyticsStore} from "@/console-new/stores";
import AppTable from "@/shared/components/AppTable.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppInfoButton from "@/shared/components/AppInfoButton.vue";
import {formatDateOnly} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";
import {useUserStore} from "@/user/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const analyticsStore = useAnalyticsStore();
		const userStore = useUserStore();
		const valuesStore = useValuesStore();
		const notificationStore = useNotificationStore();
		const currencyStore = useCurrencyStore();
		const scriptStore = useScriptStore();
		return {analyticsStore, getString: languageStore.getString, userStore, currencyStore, valuesStore, notificationStore, scriptStore};
	},
	name: "OrdersTablesAggregated",
	components: {AppTable, AppButtonIcon, AppInfoButton},
	mixins: [WindowWidth],
	props: {
		dates: {
			type: Object,
			required: true,
			validator: (value) => {
				return value.hasOwnProperty("start") && value.hasOwnProperty("end") && value.hasOwnProperty("previousStart") && value.hasOwnProperty("previousEnd") && value.hasOwnProperty("timeframeIndex");
			}
		},
	},
	data() {
		return {
			additionalCosts: [
				{
					name: this.getString('cover_charge'),
					label: 'cover_charge'
				},
				{
					name: this.getString('shipping'),
					label: 'shipping'
				},
				{
					name: this.getString('takeaway_costs'),
					label: 'takeaway_costs'
				},
				{
					name: this.getString('discount'),
					label: 'discount'
				},
				{
					name: this.getString('increases'),
					label: 'increases'
				},
				{
					name: this.getString('pos_tips'),
					label: 'pos_tips'
				},
			],
			tableHeadings: [
				{
					text: this.getString('date'),
					fieldName: 'label_full',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('gross_volume'),
					fieldName: 'readable_gross_volume',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('orders'),
					fieldName: 'orders',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('cards'),
					fieldName: 'readable_total_cards',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('cash'),
					fieldName: 'readable_total_cash',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('other'),
					fieldName: 'readable_total_other',
					sortable: true,
					cols: 1,
				},
			],
			tableHeadingsModes: [
				{
					text: this.getString('order_mode'),
					fieldName: 'order_mode',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('gross_volume'),
					fieldName: 'readable_gross_volume',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('orders'),
					fieldName: 'orders',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('cards'),
					fieldName: 'readable_total_cards',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('cash'),
					fieldName: 'readable_total_cash',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('other'),
					fieldName: 'readable_total_other',
					sortable: true,
					cols: 1,
				},
			],
			tableHeadingsTypes: [
				{
					text: this.getString('payment_type'),
					fieldName: 'type',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('gross_volume'),
					fieldName: 'readable_gross_volume',
					sortable: true,
					cols: 1,
				},
				{
					text: this.getString('payments'),
					fieldName: 'orders',
					sortable: true,
					cols: 1,
				},
			],
			tableHeadingsAdditionalCosts: [
				{
					text: this.getString('type'),
					fieldName: 'type',
					sortable: true,
					cols: 1
				},
				{
					text: this.getString("total"),
					fieldName: 'readable_total',
					sortable: true,
					cols: 1
				},
				{
					text: this.getString("quantity"),
					fieldName: 'quantity',
					sortable: true,
					cols: 1
				}
			],
			filters: [
				{
					type: "quantity",
					key: "orders",
					text: this.getString('orders'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "gross_volume",
					text: this.getString('gross_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			filtersModes: [
				{
					type: "quantity",
					key: "orders",
					text: this.getString('orders'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "gross_volume",
					text: this.getString('gross_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			filtersTypes: [
				{
					type: "quantity",
					key: "orders",
					text: this.getString('payments'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "gross_volume",
					text: this.getString('gross_volume'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			filtersAdditionalCosts: [
				{
					type: 'quantity',
					key: 'total',
					text: this.getString('total')
				}
			],
			pageSize: 10,
			currentPage: 1,
			sortField: "label_full",
			sortAsc: null,
			selectedFilters: null,
			sortFieldModes: "readable_gross_volume",
			sortAscModes: null,
			selectedFiltersModes: null,
			sortFieldTypes: "readable_gross_volume",
			sortAscTypes: null,
			selectedFiltersTypes: null,
			sortFieldStaff: "readable_orders",
			sortAscStaff: null,
			selectedFiltersStaff: null,
			sortFieldAdditionalCosts: null,
			sortAscAdditionalCosts: 'type',
			selectedFiltersAdditionalCosts: null,
			staffPageSize: 10,
			staffCurrentPage: 1,
			staffViewMode: 'creation',
			staffFiltersCreation: [
				{
					type: "quantity",
					key: "orders",
					text: this.getString('total_orders'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "quantity",
					key: "ordersNum",
					text: this.getString('orders'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "checklist",
					key: "type",
					text: this.getString('type'),
					active: false,
					values: [this.getString('online'), this.getString('station'), this.getString('staff')],
					keysArray: [this.getString('online'), this.getString('station'), this.getString('staff')],
					selectedIndexes: [],
					activeItem: 0,
					finalValue: '',
				}
			],
			staffFiltersPayment: [
				{
					type: "quantity",
					key: "payments",
					text: this.getString('total_payments'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "checklist",
					key: "type",
					text: this.getString('type'),
					active: false,
					values: [this.getString('online'), this.getString('station'), this.getString('staff')],
					keysArray: [this.getString('online'), this.getString('station'), this.getString('staff')],
					selectedIndexes: [],
					activeItem: 0,
					finalValue: '',
				}
			],
		};
	},
	computed: {
		ordersModes() {
			return this.valuesStore.ordersModes;
		},
		tableHeadingsUsers() {
			if (this.staffViewMode === 'creation') {
				return [
					{
						text: this.getString('name'),
						fieldName: 'name',
						sortable: true,
						cols: 1,
					},
					{
						text: this.getString('type'),
						fieldName: 'type',
						sortable: true,
						cols: 1,
					},
					{
						text: this.getString('total_orders'),
						fieldName: 'readable_orders',
						sortable: true,
						cols: 1,
					},
					{
						text: this.getString('orders'),
						fieldName: 'ordersNum',
						sortable: true,
						cols: 1,
					},
					{
						text: this.getString('cards'),
						fieldName: 'readable_total_cards',
						sortable: true,
						cols: 1,
					},
					{
						text: this.getString('cash'),
						fieldName: 'readable_total_cash',
						sortable: true,
						cols: 1,
					},
					{
						text: this.getString('other'),
						fieldName: 'readable_total_other',
						sortable: true,
						cols: 1,
					},
				];
			} else {
				return [
					{
						text: this.getString('name'),
						fieldName: 'name',
						sortable: true,
						cols: 1,
					},
					{
						text: this.getString('type'),
						fieldName: 'type',
						sortable: true,
						cols: 1,
					},
					{
						text: this.getString('total_payments'),
						fieldName: 'readable_payments',
						sortable: true,
						cols: 1,
					},
					{
						text: this.getString('cards'),
						fieldName: 'readable_total_cards',
						sortable: true,
						cols: 1,
					},
					{
						text: this.getString('cash'),
						fieldName: 'readable_total_cash',
						sortable: true,
						cols: 1,
					},
					{
						text: this.getString('other'),
						fieldName: 'readable_total_other',
						sortable: true,
						cols: 1,
					},
				];
			}
		},
		filtersStaff() {
			return this.staffViewMode === 'creation' ? this.staffFiltersCreation : this.staffFiltersPayment;
		},
		aggregatedData() {
			return this.analyticsStore.ordersAggregated;
		},
		parsedOrders() {
			let result = {
				datesDivision: [],
				orderModes: [],
				orderTypes: [],
				staffOrders: [],
				additionalOrderCosts: []
			};

			if (!this.aggregatedData.full || this.aggregatedData.full.length === 0) {
				return result;
			}

			const bucketColumn = this.aggregatedData.bucketColumn || 'day_bucket';
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.dates);
			
			xaxisTimes.forEach(timePoint => {
				const dateEntry = {
					label_full: timePoint.label_full || timePoint.label, // Use label_full for full date display
					rawDate: timePoint.rawDate,
					orders: 0,
					gross_volume: 0,
					total_cards: 0,
					total_cash: 0,
					total_other: 0
				};
				Object.keys(this.valuesStore.paymentsMethods).forEach(type => {
					dateEntry[`csv_${type}`] = 0;
				});
				result.datesDivision.push(dateEntry);
			});

			const filteredData = this.aggregatedData.full.filter(row => {
				const date = new Date(row[bucketColumn]);
				if (bucketColumn === 'hour_bucket') {
					const adjustedEndDate = new Date(this.dates.end);
					adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
					return date >= this.dates.start && date <= adjustedEndDate;
				}
				return date >= this.dates.start && date <= this.dates.end;
			});

			filteredData.forEach(row => {
				const bucketDate = new Date(row[bucketColumn]);
				
				if (xaxisTimes.length > 0 && bucketColumn !== 'hour_bucket') {
					bucketDate.setHours(xaxisTimes[0].rawDate.getHours(), xaxisTimes[0].rawDate.getMinutes(), 0, 0);
				}
				
				const mockOrder = { date: bucketDate };
				const index = this.analyticsStore.getDatesIndex(this.dates, xaxisTimes, mockOrder, false);
				
				if (index >= 0 && index < result.datesDivision.length) {
					result.datesDivision[index].orders += row.orders_cnt || 0;
					result.datesDivision[index].gross_volume += (row.gross_sales || 0) / 100;
					
					result.datesDivision[index].total_cards += (row.cards || 0) / 100;
					result.datesDivision[index].total_cash += (row.cash || 0) / 100;
					result.datesDivision[index].total_other += (row.other || 0) / 100;
					
					result.datesDivision[index].csv_stripe = (row.cards || 0) / 100;
					result.datesDivision[index].csv_cash = (row.cash || 0) / 100;
					result.datesDivision[index].csv_external = (row.other || 0) / 100;
				}
			});

			result.datesDivision.forEach(dateDiv => {
				dateDiv.readable_gross_volume = this.currencyStore.readableCurrencyValue(dateDiv.gross_volume, false);
				dateDiv.readable_total_cards = this.currencyStore.readableCurrencyValue(dateDiv.total_cards, false);
				dateDiv.readable_total_cash = this.currencyStore.readableCurrencyValue(dateDiv.total_cash, false);
				dateDiv.readable_total_other = this.currencyStore.readableCurrencyValue(dateDiv.total_other, false);
			});

			const costTypeStrings = {
				cover_charge: this.getString('cover_charge'),
				shipping: this.getString('shipping'),
				takeaway_costs: this.getString('takeaway_costs'),
				discount: this.getString('discount'),
				increases: this.getString('increases'),
				pos_tips: this.getString('pos_tips')
			};
			
			const additionalCostsMap = new Map();
			
			this.additionalCosts.forEach(additionalCost => {
				const costItem = {
					type: additionalCost.name,
					total: 0,
					quantity: additionalCost.label === 'cover_charge' ? 0 : '-',
					readable_total: ''
				};
				result.additionalOrderCosts.push(costItem);
				additionalCostsMap.set(additionalCost.name, result.additionalOrderCosts.length - 1);
			});

			filteredData.forEach(row => {
				if (row.cover_total > 0) {
					const index = additionalCostsMap.get(costTypeStrings.cover_charge);
					if (index !== undefined) {
						result.additionalOrderCosts[index].total += Number(row.cover_total || 0);
						result.additionalOrderCosts[index].quantity += Number(row.cover_ctn || 0);
					}
				}
				
				if (row.delivery_costs > 0) {
					const index = additionalCostsMap.get(costTypeStrings.shipping);
					if (index !== undefined) {
						result.additionalOrderCosts[index].total += Number(row.delivery_costs || 0);
						result.additionalOrderCosts[index].quantity = '-';
					}
				}
				
				if (row.takeaway_costs > 0) {
					const index = additionalCostsMap.get(costTypeStrings.takeaway_costs);
					if (index !== undefined) {
						result.additionalOrderCosts[index].total += Number(row.takeaway_costs || 0);
						result.additionalOrderCosts[index].quantity = '-';
					}
				}
				
				if (row.subtotal_discount > 0) {
					const index = additionalCostsMap.get(costTypeStrings.discount);
					if (index !== undefined) {
						result.additionalOrderCosts[index].total += Number(row.subtotal_discount || 0);
						result.additionalOrderCosts[index].quantity = '-';
					}
				}
				
				if (row.subtotal_increase > 0) {
					const index = additionalCostsMap.get(costTypeStrings.increases);
					if (index !== undefined) {
						result.additionalOrderCosts[index].total += Number(row.subtotal_increase || 0);
						result.additionalOrderCosts[index].quantity = '-';
					}
				}
				
				if (row.tip > 0) {
					const index = additionalCostsMap.get(costTypeStrings.pos_tips);
					if (index !== undefined) {
						result.additionalOrderCosts[index].total += Number(row.tip || 0);
						result.additionalOrderCosts[index].quantity = '-';
					}
				}
			});

			result.additionalOrderCosts.forEach(cost => {
				cost.readable_total = this.currencyStore.readableCurrencyValue(cost.total);
			});
			result.datesDivision = this.filterOrders(result.datesDivision);
			result.additionalOrderCosts = this.filterAdditionalCosts(result.additionalOrderCosts);

			if (this.sortField) {
				const sortField = this.sortField.startsWith('readable_') ? this.sortField.replace('readable_', '') : this.sortField;
				if (!this.sortAsc) {
					if (sortField === "label_full") {
						result.datesDivision.sort((b, a) => a.rawDate - b.rawDate);
					} else {
						result.datesDivision.sort((b, a) => a[sortField] - b[sortField]);
					}
				} else {
					if (sortField === "label_full") {
						result.datesDivision.sort((a, b) => a.rawDate - b.rawDate);
					} else {
						result.datesDivision.sort((a, b) => a[sortField] - b[sortField]);
					}
				}
			}

			if (this.sortFieldAdditionalCosts) {
				const sortField = this.sortFieldAdditionalCosts.startsWith('readable_') ? 
					this.sortFieldAdditionalCosts.replace('readable_', '') : this.sortFieldAdditionalCosts;
				if (!this.sortAscAdditionalCosts) {
					if (sortField === "type") {
						result.additionalOrderCosts.sort((b, a) => a[sortField].localeCompare(b[sortField]));
					} else {
						result.additionalOrderCosts.sort((b, a) => a[sortField] - b[sortField]);
					}
				} else {
					if (sortField === "type") {
						result.additionalOrderCosts.sort((a, b) => a[sortField].localeCompare(b[sortField]));
					} else {
						result.additionalOrderCosts.sort((a, b) => a[sortField] - b[sortField]);
					}
				}
			}

			if (this.aggregatedData.mode && this.aggregatedData.mode.length > 0) {
				const orderModesMap = new Map();
				
				for (let mode in this.ordersModes) {
					const modeItem = {
						order_mode: this.ordersModes[mode].name,
						mode_key: mode,
						orders: 0,
						gross_volume: 0,
						net_volume: 0,
						total_cards: 0,
						total_cash: 0,
						total_other: 0
					};
					Object.keys(this.valuesStore.paymentsMethods).forEach(type => {
						modeItem[`csv_${type}`] = 0;
					});
					result.orderModes.push(modeItem);
					orderModesMap.set(mode, result.orderModes.length - 1);
				}

				const filteredModeData = this.aggregatedData.mode.filter(row => {
					const date = new Date(row[bucketColumn]);
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(this.dates.end);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						return date >= this.dates.start && date <= adjustedEndDate;
					}
					return date >= this.dates.start && date <= this.dates.end;
				});

				filteredModeData.forEach(row => {
					const modeIndex = orderModesMap.get(row.order_mode);
					if (modeIndex !== undefined) {
						result.orderModes[modeIndex].orders += row.orders_cnt || 0;
						result.orderModes[modeIndex].gross_volume += (row.gross_sales || 0) / 100;
						result.orderModes[modeIndex].net_volume += (row.gross_sales || 0) / 100;

						result.orderModes[modeIndex].total_cards += (row.cards || 0) / 100;
						result.orderModes[modeIndex].total_cash += (row.cash || 0) / 100;
						result.orderModes[modeIndex].total_other += (row.other || 0) / 100;

						result.orderModes[modeIndex].csv_stripe = (row.cards || 0) / 100;
						result.orderModes[modeIndex].csv_cash = (row.cash || 0) / 100;
						result.orderModes[modeIndex].csv_external = (row.other || 0) / 100;
					}
				});

				result.orderModes.forEach(mode => {
					mode.readable_gross_volume = this.currencyStore.readableCurrencyValue(mode.gross_volume, false);
					mode.readable_net_volume = this.currencyStore.readableCurrencyValue(mode.net_volume, false);
					mode.readable_total_cards = this.currencyStore.readableCurrencyValue(mode.total_cards, false);
					mode.readable_total_cash = this.currencyStore.readableCurrencyValue(mode.total_cash, false);
					mode.readable_total_other = this.currencyStore.readableCurrencyValue(mode.total_other, false);
				});

				result.orderModes = this.filterOrdersModes(result.orderModes);

				if (this.sortFieldModes) {
					const sortField = this.sortFieldModes.startsWith('readable_') ? 
						this.sortFieldModes.replace('readable_', '') : this.sortFieldModes;
					if (!this.sortAscModes) {
						if (sortField === "order_mode") {
							result.orderModes.sort((b, a) => a[sortField].localeCompare(b[sortField]));
						} else {
							result.orderModes.sort((b, a) => a[sortField] - b[sortField]);
						}
					} else {
						if (sortField === "order_mode") {
							result.orderModes.sort((a, b) => a[sortField].localeCompare(b[sortField]));
						} else {
							result.orderModes.sort((a, b) => a[sortField] - b[sortField]);
						}
					}
				}
			}

			if (this.aggregatedData.type && this.aggregatedData.type.length > 0) {
				const orderTypesMap = new Map();
				
				for (let type in this.valuesStore.paymentsMethods) {
					if (type !== "multi" && (this.userStore.manualCode || type !== "manual")) {
						const typeItem = {
							type: this.valuesStore.paymentsMethods[type].name,
							type_key: type,
							orders: 0,
							gross_volume: 0,
							net_volume: 0
						};
						result.orderTypes.push(typeItem);
						orderTypesMap.set(type, result.orderTypes.length - 1);
					}
				}

				const filteredTypeData = this.aggregatedData.type.filter(row => {
					const date = new Date(row[bucketColumn]);
					if (bucketColumn === 'hour_bucket') {
						const adjustedEndDate = new Date(this.dates.end);
						adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
						return date >= this.dates.start && date <= adjustedEndDate;
					}
					return date >= this.dates.start && date <= this.dates.end;
				});

				filteredTypeData.forEach(row => {
					const typeIndex = orderTypesMap.get(row.payment_type);
					if (typeIndex !== undefined) {
						result.orderTypes[typeIndex].orders += row.orders_cnt || 0;
						result.orderTypes[typeIndex].gross_volume += (row.gross_sales || 0) / 100;
						result.orderTypes[typeIndex].net_volume += (row.gross_sales || 0) / 100;
					} else {
						if (!orderTypesMap.has(row.payment_type)) {
							const newTypeItem = {
								type: row.payment_type || "-",
								type_key: row.payment_type,
								orders: row.orders_cnt || 0,
								gross_volume: (row.gross_sales || 0) / 100,
								net_volume: (row.gross_sales || 0) / 100
							};
							result.orderTypes.push(newTypeItem);
							orderTypesMap.set(row.payment_type, result.orderTypes.length - 1);
						} else {
							const existingIndex = orderTypesMap.get(row.payment_type);
							result.orderTypes[existingIndex].orders += row.orders_cnt || 0;
							result.orderTypes[existingIndex].gross_volume += (row.gross_sales || 0) / 100;
							result.orderTypes[existingIndex].net_volume += (row.gross_sales || 0) / 100;
						}
					}
				});

				result.orderTypes.forEach(type => {
					type.readable_gross_volume = this.currencyStore.readableCurrencyValue(type.gross_volume, false);
					type.readable_net_volume = this.currencyStore.readableCurrencyValue(type.net_volume, false);
				});

				result.orderTypes = this.filterOrdersTypes(result.orderTypes);

				if (this.sortFieldTypes) {
					const sortField = this.sortFieldTypes.startsWith('readable_') ? 
						this.sortFieldTypes.replace('readable_', '') : this.sortFieldTypes;
					if (!this.sortAscTypes) {
						if (sortField === "type") {
							result.orderTypes.sort((b, a) => a[sortField].localeCompare(b[sortField]));
						} else {
							result.orderTypes.sort((b, a) => a[sortField] - b[sortField]);
						}
					} else {
						if (sortField === "type") {
							result.orderTypes.sort((a, b) => a[sortField].localeCompare(b[sortField]));
						} else {
							result.orderTypes.sort((a, b) => a[sortField] - b[sortField]);
						}
					}
				}
			}

			if ((this.aggregatedData.staff && this.aggregatedData.staff.length > 0) || 
			    (this.aggregatedData.payment_user && this.aggregatedData.payment_user.length > 0)) {
				
				const staffMap = {};      // For creation view (staff dimension)
				const paymentMap = {};    // For payment view (payment_user dimension)
				const userLookup = {};
				
				if (this.aggregatedData.users && this.aggregatedData.users.length > 0) {
					this.aggregatedData.users.forEach(user => {
						userLookup[user.user_id] = user;
					});
				}
				
				const filterByPeriod = (data) => {
					if (!data) return [];
					return data.filter(row => {
						const date = new Date(row[bucketColumn]);
						if (bucketColumn === 'hour_bucket') {
							const adjustedEndDate = new Date(this.dates.end);
							adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
							return date >= this.dates.start && date <= adjustedEndDate;
						}
						return date >= this.dates.start && date <= this.dates.end;
					});
				};
				
				const filteredStaffData = filterByPeriod(this.aggregatedData.staff);
				const filteredPaymentData = filterByPeriod(this.aggregatedData.payment_user);
				
				const staffTypeStrings = {
					online: this.getString('online'),
					station: this.getString('station'),
					staff: this.getString('staff')
				};
				
				filteredStaffData.forEach(row => {
					const userId = row.staff_user_id || 'online';
					if (!staffMap[userId]) {
						const user = userLookup[row.staff_user_id];
						staffMap[userId] = {
							staff_user_id: userId,
							name: userId === 'online' ? staffTypeStrings.online : (user ? user.name : userId),
							type: userId === 'online' ? staffTypeStrings.online : 
							      (user && user.station ? staffTypeStrings.station : staffTypeStrings.staff),
							orders: 0,
							ordersNum: 0,
							payments: 0,
							paymentsNum: 0,
							total_cards: 0,
							total_cash: 0,
							total_other: 0
						};
					}
					
					staffMap[userId].orders += (row.gross_sales || 0) / 100;
					staffMap[userId].ordersNum += row.orders_cnt || 0;
					staffMap[userId].total_cards += (row.cards || 0) / 100;
					staffMap[userId].total_cash += (row.cash || 0) / 100;
					staffMap[userId].total_other += (row.other || 0) / 100;
				});
				
				filteredPaymentData.forEach(row => {
					const userId = row.payment_user_id || 'online';
					if (!paymentMap[userId]) {
						const user = userLookup[row.payment_user_id];
						paymentMap[userId] = {
							staff_user_id: userId,
							name: userId === 'online' ? staffTypeStrings.online : (user ? user.name : userId),
							type: userId === 'online' ? staffTypeStrings.online : 
							      (user && user.station ? staffTypeStrings.station : staffTypeStrings.staff),
							orders: 0,
							ordersNum: 0,
							payments: 0,
							paymentsNum: 0,
							total_cards: 0,
							total_cash: 0,
							total_other: 0
						};
					}
					
					paymentMap[userId].payments += (row.gross_sales || 0) / 100;
					paymentMap[userId].paymentsNum += row.orders_cnt || 0;
					// Add payment breakdown for payment_user dimension
					paymentMap[userId].total_cards += (row.cards || 0) / 100;
					paymentMap[userId].total_cash += (row.cash || 0) / 100;
					paymentMap[userId].total_other += (row.other || 0) / 100;
				});
				
				// Store both maps separately
				const staffOrdersCreation = Object.values(staffMap);
				const staffOrdersPayment = Object.values(paymentMap);
				
				// Format readable values for creation view
				staffOrdersCreation.forEach(staff => {
					staff.readable_orders = this.currencyStore.readableCurrencyValue(staff.orders, false);
					staff.readable_payments = this.currencyStore.readableCurrencyValue(staff.payments, false);
					staff.readable_total_cards = this.currencyStore.readableCurrencyValue(staff.total_cards, false);
					staff.readable_total_cash = this.currencyStore.readableCurrencyValue(staff.total_cash, false);
					staff.readable_total_other = this.currencyStore.readableCurrencyValue(staff.total_other, false);
				});
				
				// Format readable values for payment view
				staffOrdersPayment.forEach(staff => {
					staff.readable_orders = this.currencyStore.readableCurrencyValue(staff.orders, false);
					staff.readable_payments = this.currencyStore.readableCurrencyValue(staff.payments, false);
					staff.readable_total_cards = this.currencyStore.readableCurrencyValue(staff.total_cards, false);
					staff.readable_total_cash = this.currencyStore.readableCurrencyValue(staff.total_cash, false);
					staff.readable_total_other = this.currencyStore.readableCurrencyValue(staff.total_other, false);
				});
				
				// Set the active one based on current view mode
				result.staffOrders = this.staffViewMode === 'creation' ? staffOrdersCreation : staffOrdersPayment;
				
				result.staffOrders = this.filterOrdersStaff(result.staffOrders);
				
				if (this.sortFieldStaff) {
					const sortField = this.sortFieldStaff.startsWith('readable_') ? 
						this.sortFieldStaff.replace('readable_', '') : this.sortFieldStaff;
					if (!this.sortAscStaff) {
						if (sortField === "name" || sortField === "type") {
							result.staffOrders.sort((b, a) => a[sortField].localeCompare(b[sortField]));
						} else {
							result.staffOrders.sort((b, a) => a[sortField] - b[sortField]);
						}
					} else {
						if (sortField === "name" || sortField === "type") {
							result.staffOrders.sort((a, b) => a[sortField].localeCompare(b[sortField]));
						} else {
							result.staffOrders.sort((a, b) => a[sortField] - b[sortField]);
						}
					}
				}
			}
			
			return result;
		},
		currentShownOrders() {
			const parsed = this.parsedOrders;
			return parsed.datesDivision.slice(
				(this.currentPage - 1) * this.pageSize,
				this.currentPage * this.pageSize
			);
		},
		currentShownOrderModes() {
			return this.parsedOrders.orderModes;
		},
		currentShownOrderTypes() {
			return this.parsedOrders.orderTypes;
		},
		currentShownStaff() {
			return this.parsedOrders.staffOrders.slice(
				(this.staffCurrentPage - 1) * this.staffPageSize,
				this.staffCurrentPage * this.staffPageSize
			);
		},
	},
	methods: {
		changeSort(newSortField, newSortAsc) {
			this.currentPage = 1;
			this.sortField = newSortField;
			this.sortAsc = !!newSortAsc;
			if (this.$refs['orders-table']?.$refs['pagination-btns']) {
				this.$refs['orders-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		changeFilter(newFilters) {
			this.currentPage = 1;
			this.selectedFilters = newFilters;
			if (this.$refs['orders-table']?.$refs['pagination-btns']) {
				this.$refs['orders-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		changeSortModes(newSortField, newSortAsc) {
			this.sortFieldModes = newSortField;
			this.sortAscModes = !!newSortAsc;
			if (this.$refs['orders-mode-table']?.$refs['pagination-btns']) {
				this.$refs['orders-mode-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		changeFilterModes(newFilters) {
			this.selectedFiltersModes = newFilters;
			if (this.$refs['orders-mode-table']?.$refs['pagination-btns']) {
				this.$refs['orders-mode-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		changeSortTypes(newSortField, newSortAsc) {
			this.sortFieldTypes = newSortField;
			this.sortAscTypes = !!newSortAsc;
			if (this.$refs['orders-type-table']?.$refs['pagination-btns']) {
				this.$refs['orders-type-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		changeFilterTypes(newFilters) {
			this.selectedFiltersTypes = newFilters;
			if (this.$refs['orders-type-table']?.$refs['pagination-btns']) {
				this.$refs['orders-type-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		changeSortStaff(newSortField, newSortAsc) {
			this.sortFieldStaff = newSortField;
			this.sortAscStaff = !!newSortAsc;
			if (this.$refs['orders-staff-table']?.$refs['pagination-btns']) {
				this.$refs['orders-staff-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		changeFilterStaff(newFilters) {
			this.selectedFiltersStaff = newFilters;
			if (this.$refs['orders-staff-table']?.$refs['pagination-btns']) {
				this.$refs['orders-staff-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		changeSortAdditionalCosts(newSortField, newSortAsc) {
			this.sortFieldAdditionalCosts = newSortField;
			this.sortAscAdditionalCosts = !!newSortAsc;
			if (this.$refs['orders-additional-costs-table']?.$refs['pagination-btns']) {
				this.$refs['orders-additional-costs-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		changeFilterAdditionalCosts(newFilters) {
			this.selectedFiltersAdditionalCosts = newFilters;
			if (this.$refs['orders-additional-costs-table']?.$refs['pagination-btns']) {
				this.$refs['orders-additional-costs-table'].$refs['pagination-btns'].activePage = 1;
			}
		},
		exportOrders(expExtension) {
			let sym = this.currencyStore.currCurrency.symbol;
			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm + "_" + this.getString("orders").toLowerCase() + "_" + this.getString("from").toLowerCase() + "_" + startDate + "_" + this.getString("to").toLowerCase() + "_" + endDate;
			exportToFile({
				columns: [
					{ header: this.getString('date'), key: 'label_full' },
					{ header: this.getString('gross_volume') + " " + sym, value: p => Number(p.gross_volume.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('orders'), key: 'orders', type: 'number' },
					{ header: this.getString('cards') + " " + sym, value: p => Number((p.total_cards || 0).toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('cash') + " " + sym, value: p => Number((p.total_cash || 0).toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('other') + " " + sym, value: p => Number((p.total_other || 0).toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
				],
				rows: this.parsedOrders.datesDivision,
				fileName,
				sheetName: this.getString('orders'),
				format: expExtension,
				onEmpty: () => this.notificationStore.showNotification('table-no-data'),
			});
		},
		exportModes(expExtension) {
			let sym = this.currencyStore.currCurrency.symbol;

			let columns = [
				{ header: this.getString('order_mode'), key: 'order_mode' },
				{ header: this.getString('gross_volume') + " " + sym, value: p => Number(p.gross_volume.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
				{ header: this.getString('net_volume') + " " + sym, value: p => Number(p.net_volume.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
				{ header: this.getString('orders'), key: 'orders', type: 'number' },
			];

			Object.entries(this.valuesStore.paymentsMethods).forEach(([type, value]) => {
				if (type !== "multi") {
					columns.push({
						header: value.name + " " + sym,
						value: p => Number(p["csv_" + type] ? p["csv_" + type].toFixed(2) : 0),
						type: 'number', numberFormat: '#,##0.00'
					});
				}
			});

			let extraKeys = new Set();
			this.parsedOrders.orderModes.forEach(p => {
				Object.keys(p).forEach(key => {
					if (key.startsWith("csv_")) {
						let type = key.replace("csv_", "");
						if (!this.valuesStore.paymentsMethods.hasOwnProperty(type)) {
							extraKeys.add(type);
						}
					}
				});
			});
			extraKeys.forEach(type => {
				columns.push({
					header: type,
					value: p => Number(p["csv_" + type] ? p["csv_" + type].toFixed(2) : 0),
					type: 'number', numberFormat: '#,##0.00'
				});
			});

			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm + "_orders_mode_" + this.getString("from").toLowerCase() + "_" + startDate + "_" + this.getString("to").toLowerCase() + "_" + endDate;
			exportToFile({
				columns,
				rows: this.parsedOrders.orderModes,
				fileName,
				sheetName: this.getString('modes'),
				format: expExtension,
				onEmpty: () => this.notificationStore.showNotification('table-no-data'),
			});
		},
		exportTypes(expExtension) {
			let sym = this.currencyStore.currCurrency.symbol;
			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm + "_payment_types_" + this.getString("from").toLowerCase() + "_" + startDate + "_" + this.getString("to").toLowerCase() + "_" + endDate;
			exportToFile({
				columns: [
					{ header: this.getString('payment_type'), key: 'type' },
					{ header: this.getString('gross_volume') + " " + sym, value: p => Number(p.gross_volume.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('payments'), key: 'orders', type: 'number' },
				],
				rows: this.parsedOrders.orderTypes,
				fileName,
				sheetName: this.getString('payment_type'),
				format: expExtension,
				onEmpty: () => this.notificationStore.showNotification('table-no-data'),
			});
		},
		exportStaff(expExtension) {
			let sym = this.currencyStore.currCurrency.symbol;
			let columns;
			if (this.staffViewMode === 'creation') {
				columns = [
					{ header: this.getString('name'), key: 'name' },
					{ header: this.getString('type'), key: 'type' },
					{ header: this.getString('total_orders') + " " + sym, value: p => Number(p.orders.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('orders'), key: 'ordersNum', type: 'number' },
					{ header: this.getString('cards') + " " + sym, value: p => Number(p.total_cards.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('cash') + " " + sym, value: p => Number(p.total_cash.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('other') + " " + sym, value: p => Number(p.total_other.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
				];
			} else {
				columns = [
					{ header: this.getString('name'), key: 'name' },
					{ header: this.getString('type'), key: 'type' },
					{ header: this.getString('total_payments') + " " + sym, value: p => Number(p.payments.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('cards') + " " + sym, value: p => Number(p.total_cards.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('cash') + " " + sym, value: p => Number(p.total_cash.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('other') + " " + sym, value: p => Number(p.total_other.toFixed(2)), type: 'number', numberFormat: '#,##0.00' },
				];
			}
			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let viewType = this.staffViewMode === 'creation' ? 'creation' : 'payment';
			let fileName = businessNameNorm + "_staff_" + viewType + "_" + this.getString("from").toLowerCase() + "_" + startDate + "_" + this.getString("to").toLowerCase() + "_" + endDate;
			exportToFile({
				columns,
				rows: this.parsedOrders.staffOrders,
				fileName,
				sheetName: this.getString('staff'),
				format: expExtension,
				onEmpty: () => this.notificationStore.showNotification('table-no-data'),
			});
		},
		exportAdditionalCosts(expExtension) {
			let sym = this.currencyStore.currCurrency.symbol;
			let columns = [
				{ header: this.getString('type'), key: 'type' },
				{ header: this.getString('total') + " " + sym, value: p => Number(this.currencyStore.dbToActualValue(p.total)), type: 'number', numberFormat: '#,##0.00' },
				{ header: this.getString('quantity'), key: 'quantity' },
			];

			let startDate = formatDateOnly(this.dates.start);
			let endDate = formatDateOnly(this.dates.end);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm + "_additional_costs_" + this.getString("from").toLowerCase() + "_" + startDate + "_" + this.getString("to").toLowerCase() + "_" + endDate;
			exportToFile({
				columns,
				rows: this.parsedOrders.additionalOrderCosts,
				fileName,
				sheetName: this.getString('additional_costs'),
				format: expExtension,
				onEmpty: () => this.notificationStore.showNotification('table-no-data'),
			});
		},
		filterOrders(ordersArray) {
			if (!ordersArray || !ordersArray.length || !this.selectedFilters || !Object.values(this.selectedFilters).length) {
				return ordersArray;
			}
			for (let filterField in this.selectedFilters) {
				let filterValue = this.selectedFilters[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;
				if (filterValue && ['gross_volume', 'orders'].includes(filterName)) {
					if (filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						ordersArray = ordersArray.filter(b => b[filterName] < finalValue);
					} else if (filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						ordersArray = ordersArray.filter(b => b[filterName] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						ordersArray = ordersArray.filter(b => b[filterName] === finalValue);
					}
				}
			}
			return ordersArray;
		},
		filterOrdersModes(ordersArray) {
			if (!ordersArray || !ordersArray.length || !this.selectedFiltersModes || !Object.values(this.selectedFiltersModes).length) {
				return ordersArray;
			}
			for (let filterField in this.selectedFiltersModes) {
				let filterValue = this.selectedFiltersModes[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;
				if (filterValue && ['gross_volume', 'orders'].includes(filterName)) {
					if (filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						ordersArray = ordersArray.filter(b => b[filterName] < finalValue);
					} else if (filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						ordersArray = ordersArray.filter(b => b[filterName] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						ordersArray = ordersArray.filter(b => b[filterName] === finalValue);
					}
				}
			}
			return ordersArray;
		},
		filterOrdersTypes(ordersArray) {
			if (!ordersArray || !ordersArray.length || !this.selectedFiltersTypes || !Object.values(this.selectedFiltersTypes).length) {
				return ordersArray;
			}
			for (let filterField in this.selectedFiltersTypes) {
				let filterValue = this.selectedFiltersTypes[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;
				if (filterValue && ['gross_volume', 'orders'].includes(filterName)) {
					if (filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						ordersArray = ordersArray.filter(b => b[filterName] < finalValue);
					} else if (filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						ordersArray = ordersArray.filter(b => b[filterName] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						ordersArray = ordersArray.filter(b => b[filterName] === finalValue);
					}
				}
			}
			return ordersArray;
		},
		filterOrdersStaff(ordersArray) {
			if (!ordersArray || !ordersArray.length || !this.selectedFiltersStaff || !Object.values(this.selectedFiltersStaff).length) {
				return ordersArray;
			}
			for (let filterField in this.selectedFiltersStaff) {
				let filterValue = this.selectedFiltersStaff[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;
				
				if (filterValue && ['orders', 'payments', 'ordersNum', 'paymentsNum'].includes(filterName)) {
					if (filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', ''));
						ordersArray = ordersArray.filter(b => b[filterName] < finalValue);
					} else if (filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', ''));
						ordersArray = ordersArray.filter(b => b[filterName] > finalValue);
					} else {
						let finalValue = Number(filterValue);
						ordersArray = ordersArray.filter(b => b[filterName] === finalValue);
					}
				}
				
				if (filterValue && filterName === 'type') {
					ordersArray = ordersArray.filter(b => this.selectedFiltersStaff[filterField].includes(b[filterField]));
				}
			}
			return ordersArray;
		},
		filterAdditionalCosts(additionalCostsArray) {
			if (!additionalCostsArray || !additionalCostsArray.length || !this.selectedFiltersAdditionalCosts || !Object.values(this.selectedFiltersAdditionalCosts).length) {
				return additionalCostsArray;
			}

			for (let filterField in this.selectedFiltersAdditionalCosts) {
				let filterValue = this.selectedFiltersAdditionalCosts[filterField];
				let filterName = filterField.startsWith('readable_') ? filterField.replace('readable_', '') : filterField;

				if (filterValue && filterName === 'total') {
					if (filterValue.startsWith('<')) {
						let finalValue = Number(filterValue.replace('<', '')) * 100;
						additionalCostsArray = additionalCostsArray.filter(b => b[filterField] < finalValue);
					} else if (filterValue.startsWith('>')) {
						let finalValue = Number(filterValue.replace('>', '')) * 100;
						additionalCostsArray = additionalCostsArray.filter(b => b[filterField] > finalValue);
					} else {
						let finalValue = Number(filterValue) * 100;
						additionalCostsArray = additionalCostsArray.filter(b => b[filterField] === finalValue);
					}
				}
			}
			return additionalCostsArray;
		},
		toggleStaffView() {
			this.staffViewMode = this.staffViewMode === 'creation' ? 'payment' : 'creation';
			this.staffCurrentPage = 1;
			if (this.$refs['orders-staff-table']?.$refs['pagination-btns']) {
				this.$refs['orders-staff-table'].$refs['pagination-btns'].activePage = 1;
			}
			this.selectedFiltersStaff = null;
			this.staffFiltersCreation.forEach(filter => {
				filter.active = false;
				filter.value = null;
				filter.finalValue = '';
				if (filter.type === 'checklist') {
					filter.selectedIndexes = [];
				}
			});
			this.staffFiltersPayment.forEach(filter => {
				filter.active = false;
				filter.value = null;
				filter.finalValue = '';
				if (filter.type === 'checklist') {
					filter.selectedIndexes = [];
				}
			});
		},
	},
	watch: {
		'aggregatedData.full': {
			handler(newVal) {
			},
			immediate: true
		}
	},
	mounted() {
		if (this.$refs["orders-mode-table"]) {
			this.$refs["orders-mode-table"].currentPageSize = 25;
		}
		if (this.$refs["orders-type-table"]) {
			this.$refs["orders-type-table"].currentPageSize = 25;
		}
		if (this.$refs["orders-staff-table"]) {
			this.$refs["orders-staff-table"].currentPageSize = 10;
		}
	}
}
</script>

<style scoped>
.header-title-container {
	display: flex;
	align-items: center;
	gap: 5px;
}
</style>